import * as React from 'react';
import { Eye, EyeOff } from 'lucide-react';

import { cn } from '@/lib/utils';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
    passwordToggle?: boolean;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
    ({ className, type, passwordToggle = true, ...props }, ref) => {
        const [isPasswordVisible, setIsPasswordVisible] = React.useState(false);
        const hasPasswordToggle: boolean = type === 'password' && passwordToggle;
        const input = (
            <input
                type={hasPasswordToggle && isPasswordVisible ? 'text' : type}
                className={cn(
                    'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
                    hasPasswordToggle && 'pr-10',
                    className
                )}
                ref={ref}
                {...props}
            />
        );

        if (!hasPasswordToggle) {
            return input;
        }

        const toggleLabel: string = isPasswordVisible ? 'Hide password' : 'Show password';
        return (
            <span className="relative block w-full">
                {input}
                <button
                    type="button"
                    className="absolute inset-y-0 right-0 flex w-10 items-center justify-center rounded-r-md text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
                    aria-label={toggleLabel}
                    title={toggleLabel}
                    disabled={props.disabled}
                    onClick={() => setIsPasswordVisible((visible) => !visible)}
                >
                    {isPasswordVisible ? (
                        <EyeOff className="h-4 w-4" aria-hidden="true" />
                    ) : (
                        <Eye className="h-4 w-4" aria-hidden="true" />
                    )}
                </button>
            </span>
        );
    }
);
Input.displayName = 'Input';

export { Input };
